import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown, faMinus } from '@fortawesome/free-solid-svg-icons';

/**
 * One consumption tile: a period to date, against the same elapsed span before it.
 *
 * When the baseline is nothing the tile says so rather than printing a
 * percentage. A change measured against zero is either meaningless or
 * unbounded, and on this meter both readings were badly wrong in opposite
 * directions — −84% against the whole of last month, +19,000% against the
 * matching days of it.
 *
 * @param {string} title - Period name, e.g. 'This month'.
 * @param {number} value - Total for the period to date.
 * @param {string} unit - Measurement unit.
 * @param {number|null} change - Percentage change, or null when incomparable.
 * @param {number|null} changeValue - Absolute change, or null when incomparable.
 * @param {number} baseline - Total for the matching earlier span.
 * @param {boolean} comparable - Whether the baseline supports a percentage.
 * @param {string} against - Names the span compared against.
 * @param {string} elapsed - How far into the period we are.
 */
const StatCard = ({
  title, value, unit, change, changeValue, baseline, comparable, against, elapsed,
}) => {
  const icon = change > 0 ? faArrowUp : change < 0 ? faArrowDown : faMinus;
  const tone = change > 0 ? 'positive' : change < 0 ? 'negative' : 'neutral';
  const number = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="stat-card">
      <h4 className="stat-title">{title}</h4>

      <div className="stat-value">
        {number(value)}
        <span className="stat-unit">{unit}</span>
      </div>

      {comparable ? (
        <>
          <div className={`stat-change ${tone}`}>
            <FontAwesomeIcon icon={icon} className="me-1" />
            <span className="change-percent">{Math.abs(change).toFixed(1)}%</span>
            <span className="change-value">
              ({change > 0 ? '+' : ''}{changeValue.toFixed(2)} {unit})
            </span>
          </div>
          <span className="stat-comparison">
            Vs {against} · {number(baseline)} {unit}
          </span>
        </>
      ) : (
        <>
          <div className="stat-change neutral">
            <span className="change-percent">No usable comparison</span>
          </div>
          <span className="stat-comparison">
            {against} recorded {number(baseline)} {unit}
          </span>
        </>
      )}

      <span className="stat-elapsed">{elapsed}</span>
    </div>
  );
};

export default StatCard;
